import React, { useContext, useEffect, useState } from "react";
import { VariantContextData } from "../variants/VariantBuilderContext";
import VariantBuilderPanelCollapse from "../variants/VariantBuilderPanelCollapse";
import VariantBuilderSetting from "../variants/VariantBuilderSetting";
import ReviewSettings from "../reviews/ReviewSettings";
import ElementSettingsReviewsContent from "./ElementSettingsReviewsContent";
import useReviews from "../reviews/useReviews";
import Tooltip from "../shared/Tooltip";
import ReviewRecommendationSettings from "../review_recommendations/ReviewRecommendationSettings";
import NewReviewRecommendation from "../review_recommendations/NewReviewRecommendation";

const ElementSettingsReviews = (props) => {
  const [contextData, builder] = useContext(VariantContextData);
  const element = builder.selected().object;
  const variant = contextData.objects.variant;
  const website = contextData.objects.website;
  const section = variant.sections[element.section_id];
  const step = variant.steps[section.step_id];

  const reviewIntegrationTypes = ReviewService.AVAILABLE_INTEGRATION_TYPES;
  let reviewsSourceOptions = [
    {
      value: "ConvertFlow",
      text: "ConvertFlow",
    },
  ];

  reviewIntegrationTypes.forEach((integrationType) => {
    const integrationObject = Object.values(website.integrations).find(
      (i) => i.integration_type == integrationType && i.status == true);

    let disabled = true;
    if (integrationObject) {
      disabled = false;
    }

    reviewsSourceOptions.push({
      value: integrationType,
      text: `${integrationType} (Beta)`,
      disabled: disabled,
    });
  });

  const review_recommendations = Object.values(variant.review_recommendations || {})
    .filter((r) => r.element_id == element.id && r.toBeDeleted !== true);

  const { Reviews } = useReviews();

  const [availableReviews, setAvailableReviews] = useState([]);
  const [elementReviews, setElementReviews] = useState([]);

  useEffect(() => {
    const integrationType = element.options["reviews-source"] || "ConvertFlow";
    const integrationObject = Object.values(website.integrations).find(
      (i) => i.integration_type == integrationType && i.status == true
    );
    const integrationService = new ReviewsIntegrationsService(integrationType, integrationObject, website.id, true);

    new Promise(async (resolve, reject) => {
      const reviewsResponse = await integrationService.getStoreReviews();
      setAvailableReviews(reviewsResponse);

      const reviewsIds = element.options["reviews-ids"]?.split(",")?.filter(id => id) || [];
      const elementReviews = reviewsIds
        .map(id => reviewsResponse.find(review => review.id.toString() === id))
        .filter(review => review) || [];
      setElementReviews(elementReviews);

      Reviews.sortableStart(element);
    })
  }, [element.options["reviews-ids"], element.options['reviews-source'], element.options["reviews-last-updated"]]);

  const createReview = _.debounce(() => {
    let element_reviews = element.options["reviews-ids"]?.split(",")?.filter(id => id !== '') || [];

    Reviews.create({
      title: `Review ${element_reviews.length + 1}`,
    }, (review) => {
      element_reviews.push(review.id);

      builder.update([
        {
          object_type: "elements",
          object_id: element.id,
          setting_name: "[options][reviews-ids]",
          value: element_reviews.join(",")
        },
        {
          object_type: "elements",
          object_id: element.id,
          setting_name: "[options][reviews-last-updated]",
          value: Date.now().toString()
        }
      ]);

      // Wait for the review to be rendered in the DOM, then open the edit modal
      setTimeout(() => {
        dispatchCustomEvent("focusItem", {
          parent_id: element.id,
          item_type: "reviews",
          item_id: review.id
        });
      }, 300);
    });
  }, 200);

  return (
    <React.Fragment>
      {element.element_type == "reviews" && (
        <div className="col-xs-12">
          <React.Fragment>
            <VariantBuilderPanelCollapse name="Reviews" open={true}>
              <VariantBuilderSetting
                object={element}
                setting_name="[options][reviews-source]"
                setting_type="dropdown"
                label="Reviews Source"
                object_type="elements"
                object_id={element.id}
                value={
                  element.options["reviews-source"]
                    ? element.options["reviews-source"]
                    : "ConvertFlow"
                }
                options={reviewsSourceOptions.sort((a, b) => a.disabled ? 1 : b.disabled ? -1 : 0)}
              />

              <VariantBuilderSetting
                object={element}
                setting_name="[options][reviews-filter-type]"
                setting_type="dropdown"
                label="Reviews Filter Type"
                object_type="elements"
                object_id={element.id}
                value={element.options["reviews-filter-type"]}
                options={[
                  { value: "", text: "Selection" },
                  { value: "filter", text: "Filter" },
                ]}
              />

              <VariantBuilderSetting
                object={element}
                object_type="elements"
                object_id={element.id}
                label="Reviews"
                right_button_tag={element.options["reviews-source"] == "ConvertFlow" ? [
                  {                                                                                                                                                                                                                                                                                                                    
                    text: "Manage",
                    className: "border-[1px] border-solid py-1 px-5 rounded-lg !text-black dark:text-gray-400 bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800",
                    onClick: () => {
                      window.open(`/websites/${website.id}/reviews`, "_blank");
                    }
                  },
                  {
                    text: "+ New",
                    className: "border-[1px] border-solid py-1 px-5 rounded-lg text-base bg-black border-black dark:bg-blue-600 dark:border-blue-600 text-white",
                    onClick: createReview
                  }
                ] : {}}
                setting_name="[options][reviews-ids]"
                setting_type="select"
                value={element.options["reviews-ids"]}
                onEditItem={(review) => {
                  dispatchCustomEvent("openReviewSettings", {
                    review_id: review.value
                  });
                }}
                condition={[undefined, "", "selection"].includes(element.options["reviews-filter-type"])}
                options={availableReviews.map((review) => ({
                  value: review.id.toString(),
                  text: review.customer_name && review.title
                    ? `${review.customer_name} - ${review.title}`
                    : review.customer_name
                      ? review.customer_name
                      : review.title
                        ? review.title
                        : `Review ${review.id}`
                }))}
                multi_select={true}
                allow_empty={true}
              />

              {/* Render Review Settings modals if they are open */}
              {Object.values(elementReviews)
                .map((review, i) => {
                  return (
                    <React.Fragment key={"review-settings-" + review.id}>
                      <ReviewSettings
                        review={review}
                        element={element}
                        website={website}
                        {...props}
                      />
                    </React.Fragment>
                  );
                })}

              <VariantBuilderSetting
                object={element}
                setting_name="[options][reviews-filters]"
                setting_type="select"
                label="Filters"
                object_type="elements"
                object_id={element.id}
                show_reset={true}
                value={element.options["reviews-filters"]}
                options={
                  [
                    {
                      value: "keyword",
                      text: "Keyword",
                    },
                    {
                      value: "rating",
                      text: "Rating",
                    },
                    {
                      value: "video",
                      text: "Video",
                    },
                    {
                      value: "date",
                      text: "Date",
                    }
                  ]
                }
                multi_select={true}
                condition={element.options["reviews-filter-type"] == "filter"}
              />

              <VariantBuilderSetting
                object={element}
                object_id={element.id}
                setting_name="[options][reviews-sorting-operator]"
                setting_type="options_buttons"
                object_type="elements"
                label="Sorting Operator"
                options={
                  [

                    { value: "asc", title: "Ascending", height: 40 },
                    { value: "desc", title: "Descending", height: 40 },
                  ]
                }
                value={element.options["reviews-sorting-operator"]}
                placeholder={"asc"}
              />

              <VariantBuilderSetting
                object={element}
                setting_name="[options][reviews-sorting]"
                setting_type="select"
                label="Sort"
                object_type="elements"
                object_id={element.id}
                value={element.options["reviews-sorting"] ? element.options["reviews-sorting"] : "date"}
                show_reset={true}
                options={
                  [
                    { value: "date", text: "Date" },
                    { value: "position", text: "Position" },
                    { value: "rating", text: "Rating" },
                    { value: "title", text: "Title" },
                  ]
                }
              />

              <VariantBuilderSetting
                object={element}
                setting_name="[options][reviews-filters-keyword]"
                setting_type="text"
                label="Keywords"
                object_type="elements"
                object_id={element.id}
                value={element.options["reviews-filters-keyword"]}
                placeholder="good, excellent, amazing (comma-separated)"
                condition={
                  element.options["reviews-filter-type"] == "filter" &&
                  element.options["reviews-filters"]?.includes("keyword") == true
                }
              />

              <VariantBuilderSetting
                object={element}
                setting_name="[options][reviews-filters-rating-condition]"
                setting_type="dropdown"
                label="Rating Condition"
                object_type="elements"
                object_id={element.id}
                value={element.options["reviews-filters-rating-condition"]}
                options={[
                  { value: "greater_than", text: "Greater than" },
                  { value: "less_than", text: "Less than" },
                  { value: "equal_to", text: "Equal to" },
                ]}
                condition={
                  element.options["reviews-filter-type"] == "filter" &&
                  element.options["reviews-filters"]?.includes("rating") == true
                }
              />

              <VariantBuilderSetting
                object={element}
                setting_name="[options][reviews-filters-rating-value]"
                setting_type="text"
                label="Rating Value"
                object_type="elements"
                object_id={element.id}
                value={element.options["reviews-filters-rating-value"]}
                placeholder="5"
                condition={
                  element.options["reviews-filter-type"] == "filter" &&
                  element.options["reviews-filters"]?.includes("rating") == true
                }
              />

              <VariantBuilderSetting
                object={element}
                label="Has Video"
                setting_name="[options][reviews-filters-has-video]"
                setting_type="checkbox"
                object_type="elements"
                object_id={element.id}
                value={element.options["reviews-filters-has-video"] ? element.options["reviews-filters-has-video"] : "false"}
                condition={
                  element.options["reviews-filter-type"] == "filter" &&
                  element.options["reviews-filters"]?.includes("video") == true
                }
              />

              <VariantBuilderSetting
                object={element}
                label="Date Condition"
                setting_name="[options][reviews-filters-date-condition]"
                setting_type="dropdown"
                object_type="elements"
                object_id={element.id}
                value={element.options["reviews-filters-date-condition"]}
                options={[
                  { value: "greater_than", text: "Greater than" },
                  { value: "less_than", text: "Less than" },
                  { value: "equal_to", text: "Equal to" },
                ]}
                condition={
                  element.options["reviews-filter-type"] == "filter" &&
                  element.options["reviews-filters"]?.includes("date") == true
                }
              />

              <VariantBuilderSetting
                object={element}
                label="Date Value"
                setting_name="[options][reviews-filters-date-value]"
                setting_type="date"
                object_type="elements"
                object_id={element.id}
                value={element.options["reviews-filters-date-value"]}
                placeholder="2021-01-01"
                condition={
                  element.options["reviews-filter-type"] == "filter" &&
                  element.options["reviews-filters"]?.includes("date") == true
                }
              />

              <VariantBuilderSetting
                object={element}
                setting_name="[options][reviews-max-count]"
                setting_type="range"
                label="Maximum Number of Reviews"
                object_type="elements"
                object_id={element.id}
                value={element.options["reviews-max-count"]}
                placeholder="6"
                min={1}
                max={100}
                range_step="1"
                allow_empty={true}
                condition={element.options["reviews-filter-type"] == "filter"}
                show_reset={true}
              />

              <div className="col-xs-12">
                <div className="w-full my-5 relative text-black dark:text-white">
                  <span className="text-xl">Conditional Reviews:</span>

                  <Tooltip
                    tooltip={`Use conditions to override your default review with another review filter. The first review that meets the conditions will be used.`}
                  />
                </div>

                {review_recommendations.map((review_recommendation) => {
                  return (
                    <ReviewRecommendationSettings
                      key={"review-recommendation-" + review_recommendation.id}
                      step={step}
                      section={section}
                      element={element}
                      review_recommendation={review_recommendation}
                    />
                  );
                })}
              </div>
              <NewReviewRecommendation element={element} />

              <VariantBuilderSetting
                object={element}
                object_id={element.id}
                label="Empty Message"
                setting_name="[options][reviews-empty-message]"
                placeholder="No reviews found"
                setting_type="text"
                object_type="elements"
                value={element.options["reviews-empty-message"]}
              />

              <VariantBuilderSetting
                object={element}
                setting_name="[options][reviews-container-width]"
                setting_type="range"
                object_type="elements"
                object_id={element.id}
                label="Container Width"
                value={element.options["reviews-container-width"]}
                placeholder="auto"
                min={0}
                max={2560}
                range_step="1"
                allow_empty={true}
                device="desktop"
                show_reset={true}
              />

              <VariantBuilderSetting
                object={element}
                setting_name="[options][reviews-container-width-mobile]"
                setting_type="range"
                object_type="elements"
                object_id={element.id}
                label="Container Width"
                value={element.options["reviews-container-width-mobile"]}
                placeholder="auto"
                min={0}
                max={2560}
                range_step="1"
                allow_empty={true}
                device="mobile"
                show_reset={true}
              />

              <VariantBuilderSetting
                object={element}
                setting_name="[options][content-flow]"
                setting_type="dropdown"
                object_type="elements"
                object_id={element.id}
                value={element.options["content-flow"]}
                label="Content Flow"
                options={[
                  { value: "", text: "Rows" },
                  { value: "collage", text: "Collage", disabled: element.options['slider-enabled'] == 'true' },
                ]}
                condition={element.options["slider-enabled"] !== "true"}
              />

              <VariantBuilderSetting
                object={element}
                setting_name="[options][slider-enabled]"
                setting_type="checkbox"
                object_type="elements"
                object_id={element.id}
                value={
                  element.options["slider-enabled"]
                    ? element.options["slider-enabled"]
                    : "true"
                }
                label="Slider Enabled"
              />

              <VariantBuilderSetting
                object={element}
                setting_name="[options][slider-show-indicators]"
                setting_type="checkbox"
                object_type="elements"
                object_id={element.id}
                value={
                  element.options["slider-show-indicators"]
                    ? element.options["slider-show-indicators"]
                    : "true"
                }
                label="Slider Show Indicators"
                condition={!element.options["slider-enabled"] || element.options["slider-enabled"] == "true"}
              />

              <VariantBuilderSetting
                object={element}
                setting_name="[options][slider-show-arrows]"
                setting_type="checkbox"
                object_type="elements"
                object_id={element.id}
                value={
                  element.options["slider-show-arrows"]
                    ? element.options["slider-show-arrows"]
                    : "true"
                }
                label="Slider Show Arrows"
                condition={!element.options["slider-enabled"] || element.options["slider-enabled"] == "true"}
              />

              <VariantBuilderSetting
                object={element}
                setting_name="[options][reviews-per-row]"
                setting_type="range"
                object_type="elements"
                object_id={element.id}
                value={element.options["reviews-per-row"]}
                placeholder={3}
                label={`Number of reviews per ${element.options["slider-enabled"] == "false" ? "row" : "slide"}`}
                min={1}
                max={10}
                step={1}
                allow_empty={true}
                device="desktop"
                show_reset={true}
                condition={element.options["reviews-layout"] == "horizontal"}
              />

              <VariantBuilderSetting
                object={element}
                setting_name="[options][reviews-per-row-mobile]"
                setting_type="range"
                object_type="elements"
                object_id={element.id}
                value={element.options["reviews-per-row-mobile"]}
                placeholder={1}
                label={`Number of reviews per ${element.options["slider-enabled"] == "false" ? "row" : "slide"}`}
                min={1}
                max={10}
                step={1}
                allow_empty={true}
                device="mobile"
                show_reset={true}
                condition={element.options["reviews-layout"] == "horizontal"}
              />

              <VariantBuilderSetting
                object={element}
                setting_name="[options][slider-style]"
                setting_type="dropdown"
                object_type="elements"
                object_id={element.id}
                value={element.options["slider-style"]}
                label="Slider Style"
                options={[
                  { value: "", text: "Default" },
                  { value: "inline", text: "Inline" },
                ]}
                condition={element.options["slider-enabled"] == "true"}
              />

              <VariantBuilderSetting
                object={element}
                setting_name="[options][reviews-arrow-size]"
                setting_type="range"
                object_type="elements"
                object_id={element.id}
                label="Arrow Size"
                value={
                  element.options["reviews-arrow-size"]
                    ? element.options["reviews-arrow-size"]
                    : undefined
                }
                placeholder="12"
                min={1}
                max={40}
                range_step="1"
                device="desktop"
                allow_empty={true}
                condition={(!element.options["slider-enabled"] || element.options["slider-enabled"] == "true") && (!element.options["slider-show-arrows"] || element.options["slider-show-arrows"] == "true")}
                show_reset={true}
              />

              <VariantBuilderSetting
                object={element}
                setting_name="[options][reviews-arrow-size-mobile]"
                setting_type="range"
                object_type="elements"
                object_id={element.id}
                label="Arrow Size"
                value={
                  element.options["reviews-arrow-size-mobile"]
                    ? element.options["reviews-arrow-size-mobile"]
                    : undefined
                }
                placeholder="12"
                min={1}
                max={40}
                range_step="1"
                device="mobile"
                allow_empty={true}
                condition={(!element.options["slider-enabled"] || element.options["slider-enabled"] == "true") && (!element.options["slider-show-arrows"] || element.options["slider-show-arrows"] == "true")}
                show_reset={true}
              />
            </VariantBuilderPanelCollapse>

            <VariantBuilderPanelCollapse name="Content" open={true}>
              <VariantBuilderSetting
                object={element}
                object_id={element.id}
                label="Layout"
                setting_name="[options][reviews-layout]"
                setting_type="layout"
                object_type="elements"
                value={
                  element.options["reviews-layout"]
                    ? element.options["reviews-layout"]
                    : variant.options["reviews-layout"]
                    ? variant.options["reviews-layout"]
                    : undefined
                }
                placeholder={variant.options["reviews-layout"]}
                valueMappings={{
                  responsive: "horizontal",
                  stacked: "vertical",
                }}
              />
              <ElementSettingsReviewsContent element={element} {...props}/>

              <VariantBuilderSetting
                object={element}
                label="Inner Content Alignment"
                setting_name="[options][cf-inner-alignment]"
                setting_type="alignment"
                object_type="elements"
                object_id={element.id}
                value={
                  element.options["cf-inner-alignment"]
                    ? element.options["cf-inner-alignment"]
                    : "left"
                }
                device="desktop"
                show_reset={true}
                valueMappings={{
                  left: "left",
                  center: "center",
                  right: "right",
                }}
              />

              <VariantBuilderSetting
                object={element}
                label="Inner Content Alignment"
                setting_name="[options][cf-inner-alignment-mobile]"
                setting_type="alignment"
                object_type="elements"
                object_id={element.id}
                value={
                  element.options["cf-inner-alignment-mobile"]
                    ? element.options["cf-inner-alignment-mobile"]
                    : ""
                }
                device="mobile"
                show_reset={true}
                valueMappings={{
                  left: "left",
                  center: "center",
                  right: "right",
                }}
              />
            </VariantBuilderPanelCollapse>
          </React.Fragment>
        </div>
      )}
    </React.Fragment>
  );
};

export default ElementSettingsReviews;
